(() => {
  'use strict';
  if (customElements.get('tazty-box-builder')) return;
  const { Catalog, Selection, weightMoment } = window.TaztyBoxCore;
  const node = (tag, className, text) => {
    const result = document.createElement(tag);
    if (className) result.className = className;
    if (text != null) result.textContent = text;
    return result;
  };
  class BoxBuilder extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.config = JSON.parse(this.querySelector('[data-box-config]').textContent);
      this.selection = new Selection();
      this.variants = new Map();
      this.filter = 'all'; this.query = ''; this.limit = 24; this.version = 0;
      this.submitting = false; this.checkoutUncertain = false;
      this.controller = new AbortController();
      this.catalog = new Catalog(this.config.collections, (collection, page) => this.fetchPage(collection, page), this.config.all_collection);
      this.grid = this.querySelector('[data-box-grid]');
      this.more = this.querySelector('[data-box-more]');
      this.checkout = this.querySelector('[data-box-checkout]');
      this.addEventListener('click', this.onClick = event => this.handleClick(event));
      this.addEventListener('change', this.onChange = event => {
        if (!event.target.matches('[data-box-variant]')) return;
        this.variants.set(event.target.dataset.productId, event.target.value);
        this.refreshCards(); this.announce('');
      });
      this.querySelector('[data-box-search]').addEventListener('input', this.onSearch = event => {
        clearTimeout(this.searchTimer);
        this.version++; // A slower previous search must not replace newer results.
        this.query = event.target.value;
        this.searchTimer = setTimeout(() => this.resetView(), 250);
      });
      this.observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !this.loading && !this.loadError &&
            this.catalog.hasMore(this.filter, this.query, this.shown || 0)) this.loadMore();
      }, { rootMargin: '0px 0px 250px 0px' }) : null;
      this.observer?.observe(this.querySelector('[data-box-sentinel]'));
      this.renderSummary();
      this.loadView();
    }
    disconnectedCallback() {
      this.version++; this.controller?.abort(); this.observer?.disconnect(); clearTimeout(this.searchTimer);
      this.removeEventListener('click', this.onClick); this.removeEventListener('change', this.onChange);
      this.querySelector('[data-box-search]')?.removeEventListener('input', this.onSearch);
      this.started = false;
    }
    money(cents) {
      return new Intl.NumberFormat(this.config.locale || 'en', { style: 'currency', currency: this.config.currency }).format(cents / 100);
    }
    weight(variant) {
      if (!(Number(variant.grams) > 0)) return '';
      const value = Number(variant.weight) || Number(variant.grams);
      return new Intl.NumberFormat(this.config.locale || 'en', { maximumFractionDigits: 3 }).format(value) + ' ' + (variant.weight_unit || 'g');
    }
    variant(product) {
      const selected = this.selection.lines.get(product.id)?.variant.id || this.variants.get(product.id) || product.selected_variant;
      return product.variants.find(v => String(v.id) === String(selected)) || product.variants.find(v => v.available) || product.variants[0];
    }
    async fetchPage(collection, page) {
      const url = new URL(collection.url, location.origin);
      if (url.origin !== location.origin) throw new Error(this.config.load_error);
      url.searchParams.set('view', 'tazty-box-data'); url.searchParams.set('page', page);
      // Keep draft-theme context when it is explicitly present in a preview URL.
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.any([this.controller.signal, AbortSignal.timeout(15000)]) });
      if (!response.ok) throw new Error(this.config.load_error);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const script = parsed.querySelector('[data-box-catalog]');
      if (!script) throw new Error(this.config.load_error);
      const data = JSON.parse(script.textContent);
      if (data.currency !== this.config.currency) throw new Error(this.config.currency_error);
      return data;
    }
    announce(text) { this.querySelector('[data-box-status]').textContent = text; }
    resetView() {
      this.limit = 24; this.shown = 0;
      this.grid.replaceChildren();
      this.querySelector('[data-box-results]').textContent = '';
      this.more.hidden = true;
      this.loadView();
    }
    loadMore() { if (!this.loading) { this.limit += 24; this.loadView(); } }
    async loadView() {
      const version = ++this.version;
      this.loading = true; this.loadError = false; this.more.disabled = true;
      this.querySelector('[data-box-loading]').hidden = false;
      this.querySelector('[data-box-empty]').hidden = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.querySelectorAll('[data-box-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.boxFilter === this.filter)));
      try {
        const products = await this.catalog.ensure(this.filter, this.query, this.limit, () => version === this.version && this.isConnected);
        if (version !== this.version || !this.isConnected) return;
        this.grid.replaceChildren(...products.slice(0, this.limit).map(product => this.productCard(product)));
        this.shown = Math.min(products.length, this.limit);
        this.refreshCards();
        this.querySelector('[data-box-results]').textContent = this.config.results_text.replace('[count]', this.shown);
        this.querySelector('[data-box-empty]').hidden = this.shown !== 0 || this.catalog.streams.size === 0;
        this.more.hidden = !this.catalog.hasMore(this.filter, this.query, this.shown);
        this.announce('');
        this.grid.dispatchEvent(new CustomEvent('tazty:products:loaded', { bubbles: true }));
      } catch (error) {
        if (version !== this.version || !this.isConnected) return;
        this.loadError = true; this.announce(error.message || this.config.load_error);
        this.more.hidden = false; // Manual retry; never loop on a failed request.
      } finally {
        if (version === this.version && this.isConnected) {
          this.loading = false; this.more.disabled = false;
          this.more.textContent = this.loadError ? this.config.retry_label : this.config.more_label;
          this.querySelector('[data-box-loading]').hidden = true; this.grid.setAttribute('aria-busy', 'false');
        }
      }
    }
    button(text, action, id, focus) {
      const button = node('button', '', text);
      button.type = 'button'; button.dataset.boxAction = action;
      if (id) button.dataset.productId = id;
      if (focus) button.dataset.focusKey = focus;
      return button;
    }
    productCard(product) {
      const card = node('article', 'tz-box__card'); card.dataset.boxProduct = product.id;
      const media = node('a', 'tz-box__media'); media.href = product.url;
      if (product.image) {
        const image = node('img'); image.src = product.image; image.alt = product.title; image.loading = 'lazy'; image.width = 600; image.height = 600; media.append(image);
      } else media.append(node('span', 'tz-box__image-placeholder', this.config.no_image));
      if (this.config.show_badges && product.badges) { const badges = node('span', 'tz-box__badges'); badges.innerHTML = product.badges; media.append(badges); }
      card.append(media);
      const copy = node('div', 'tz-box__copy');
      if (product.type || product.vendor) copy.append(node('span', 'tz-box__meta', product.type || product.vendor));
      const heading = node('h3'); const link = node('a', '', product.title); link.href = product.url; heading.append(link); copy.append(heading);
      if (this.config.show_description && product.description) copy.append(node('p', 'tz-box__description', product.description));
      if (product.variants.length > 1) {
        const label = node('label', 'tz-box__variant-label', this.config.variant_label);
        const select = node('select'); select.dataset.boxVariant = ''; select.dataset.productId = product.id;
        select.dataset.focusKey = 'variant-' + product.id;
        product.variants.forEach(variant => { const option = node('option', '', variant.title + (variant.available ? '' : ' — ' + this.config.sold_out)); option.value = variant.id; option.disabled = !variant.available; select.append(option); });
        label.append(select); copy.append(label);
      }
      copy.append(node('p', 'tz-box__weight'));
      if (this.config.show_dietary && product.dietary) { const dietary = node('div', 'tz-box__dietary'); dietary.innerHTML = product.dietary; copy.append(dietary); }
      const add = this.button('', 'plus', product.id, 'add-' + product.id); add.className = 'tz-box__add'; add.append(node('span'), node('strong')); copy.append(add);
      const controls = node('div', 'tz-box__controls');
      controls.append(this.button('−', 'minus', product.id, 'minus-' + product.id), node('strong'), this.button('+', 'plus', product.id, 'plus-' + product.id));
      controls.children[0].setAttribute('aria-label', this.config.decrease_label + ' ' + product.title);
      controls.children[2].setAttribute('aria-label', this.config.increase_label + ' ' + product.title);
      copy.append(controls); card.append(copy);
      return card;
    }
    refreshCards() {
      this.grid.querySelectorAll('[data-box-product]').forEach(card => {
        const product = this.catalog.products.get(card.dataset.boxProduct), variant = this.variant(product);
        if (!variant) return;
        const line = this.selection.lines.get(product.id), quantity = line?.quantity || 0, weight = this.weight(variant);
        card.classList.toggle('is-selected', quantity > 0);
        card.querySelector('.tz-box__weight').textContent = weight ? this.config.weight_text.replace('[weight]', weight) : this.config.item_text;
        const add = card.querySelector('.tz-box__add');
        add.hidden = quantity > 0; add.disabled = !variant.available || this.submitting || this.checkoutUncertain;
        add.children[0].textContent = variant.available ? (weight ? this.config.add_weight.replace('[weight]', weight) : this.config.add_item) : this.config.sold_out;
        add.children[1].textContent = this.money(Number(variant.price));
        add.setAttribute('aria-label', add.children[0].textContent + ' — ' + product.title);
        const controls = card.querySelector('.tz-box__controls'); controls.hidden = !quantity;
        controls.querySelector('strong').textContent = quantity;
        controls.querySelectorAll('button').forEach(button => {
          button.disabled = this.submitting || this.checkoutUncertain || (button.dataset.boxAction === 'plus' && variant.max != null && quantity + (Number(variant.step) || 1) > Number(variant.max));
        });
        const select = card.querySelector('select');
        if (select) { select.value = variant.id; select.disabled = quantity > 0 || this.submitting || this.checkoutUncertain; select.title = quantity ? this.config.variant_hint : ''; }
      });
    }
    handleClick(event) {
      const filter = event.target.closest('[data-box-filter]');
      if (filter && this.contains(filter)) { this.filter = filter.dataset.boxFilter; this.resetView(); return; }
      const action = event.target.closest('[data-box-action]');
      if (!action || !this.contains(action) || action.disabled) return;
      if (action.dataset.boxAction === 'more') { if (this.loadError) this.loadView(); else this.loadMore(); return; }
      if (action.dataset.boxAction === 'checkout') { this.submit(); return; }
      if (this.submitting || this.checkoutUncertain) return;
      const focus = action.dataset.focusKey;
      try {
        if (action.dataset.boxAction === 'clear') this.selection.lines.clear();
        else {
          const product = this.catalog.products.get(action.dataset.productId) || this.selection.lines.get(action.dataset.productId)?.product;
          if (!product) return;
          this.selection.change(product, this.variant(product), action.dataset.boxAction === 'plus' ? 1 : -1);
        }
        this.refreshCards(); this.renderSummary(); this.announce('');
        if (focus) {
          const replacement = [...this.querySelectorAll('[data-focus-key]')].find(el => el.dataset.focusKey === focus && !el.closest('[hidden]'));
          (replacement || this.querySelector('[data-box-search]'))?.focus({ preventScroll: true });
        }
      } catch (error) { this.announce(error.message); }
    }
    renderWeightMoment(allWeightsKnown) {
      const indicator = this.querySelector('[data-box-weight-moment]');
      if (!indicator) return;
      indicator.hidden = !this.config.show_weight_moment || !allWeightsKnown;
      if (indicator.hidden) return;
      const grams = this.selection.grams();
      const weight = new Intl.NumberFormat(this.config.locale, { maximumFractionDigits: 2 }).format(grams >= 1000 ? grams / 1000 : grams) + (grams >= 1000 ? ' kg' : ' g');
      const moment = weightMoment(grams, [1, 2, 3].map(n => ({ grams: this.config['weight_milestone_' + n], message: this.config['weight_message_' + n] })));
      this.querySelector('[data-box-moment-weight]').textContent = weight;
      this.querySelector('[data-box-moment-message]').textContent = (moment.message || this.config.weight_start_message || '').replaceAll('[weight]', weight);
      this.querySelector('[data-box-moment-fill]').style.width = moment.progress + '%';
    }
    renderSummary() {
      const lines = [...this.selection.lines.values()], count = this.selection.count();
      const preview = this.querySelector('[data-box-preview]');
      preview.replaceChildren(...lines.map(line => {
        const tile = node('span', 'tz-box__preview-tile'); tile.dataset.previewProduct = line.product.id;
        if (line.product.image) { const image = node('img'); image.src = line.product.image; image.alt = line.product.title; tile.append(image); }
        tile.append(node('b', '', '×' + line.quantity)); return tile;
      }));
      while (preview.children.length < 4) { const empty = node('span', 'tz-box__preview-empty', '+'); empty.setAttribute('aria-hidden', 'true'); preview.append(empty); }
      this.querySelector('[data-box-selections]').replaceChildren(...lines.map(line => {
        const row = node('div', 'tz-box__selection'); row.dataset.selectionProduct = line.product.id;
        if (line.product.image) { const image = node('img'); image.src = line.product.image; image.alt = ''; row.append(image); }
        const copy = node('div', 'tz-box__selection-copy'); copy.append(node('strong', '', line.product.title), node('span', '', this.money(line.quantity * Number(line.variant.price))));
        if (line.variant.title !== 'Default Title') copy.append(node('small', '', line.variant.title));
        const controls = node('div', 'tz-box__selection-controls');
        controls.append(this.button('−', 'minus', line.product.id, 'summary-minus-' + line.product.id), node('b', '', line.quantity), this.button('+', 'plus', line.product.id, 'summary-plus-' + line.product.id));
        controls.children[0].setAttribute('aria-label', this.config.decrease_label + ' ' + line.product.title);
        controls.children[2].setAttribute('aria-label', this.config.increase_label + ' ' + line.product.title);
        controls.querySelectorAll('button').forEach(b => b.disabled = this.submitting || this.checkoutUncertain || (b.dataset.boxAction === 'plus' && line.variant.max != null && line.quantity + (Number(line.variant.step) || 1) > Number(line.variant.max)));
        row.append(copy, controls); return row;
      }));
      this.querySelector('[data-box-summary-empty]').hidden = lines.length > 0;
      this.querySelector('[data-box-count]').textContent = this.config.count_text.replace('[count]', count);
      this.querySelector('[data-box-total]').textContent = this.money(this.selection.price());
      const allWeightsKnown = lines.every(line => Number(line.variant.grams) > 0);
      this.renderWeightMoment(allWeightsKnown);
      this.querySelector('[data-box-total-weight]').textContent = this.config.show_total_weight && count && allWeightsKnown ? this.config.total_weight_text.replace('[weight]', new Intl.NumberFormat(this.config.locale).format(this.selection.grams()) + ' g') : '';
      this.checkout.disabled = this.submitting || this.checkoutUncertain || count < this.config.minimum_items;
      this.checkout.textContent = this.submitting ? this.config.checkout_loading : count < this.config.minimum_items ? this.config.minimum_text.replace('[count]', this.config.minimum_items) : this.config.checkout_label + ' · ' + this.money(this.selection.price());
      this.querySelector('[data-box-action="clear"]').disabled = !count || this.submitting || this.checkoutUncertain;
    }
    async submit() {
      if (this.submitting || this.checkoutUncertain || this.selection.count() < this.config.minimum_items) return;
      this.submitting = true; this.refreshCards(); this.renderSummary(); this.announce('');
      const addedCount = this.selection.count();
      let attempted = false;
      try {
        const boxId = crypto.randomUUID();
        const items = this.selection.items(boxId, this.config.box_label);
        attempted = true;
        const response = await fetch(this.config.root + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(20000),
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.config.checkout_error);
      } catch (error) {
        // Add requests can partially succeed or time out after being processed.
        // Do not blindly retry and duplicate products; keep selections for review.
        this.checkoutUncertain = attempted;
        this.querySelector('[data-box-cart-review]').hidden = !attempted;
        this.announce((error.message || this.config.checkout_error) + (attempted ? ' ' + this.config.review_error : ''));
        this.submitting = false; this.refreshCards(); this.renderSummary();
        return;
      }
      // The add succeeded. Clear only the builder selection, never the existing cart.
      // A failed follow-up refresh must not turn a successful add into a retry.
      this.selection.lines.clear();
      this.variants.clear();
      let cart = null;
      try {
        const response = await fetch(this.config.root + 'cart.js', {
          credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(10000)
        });
        if (response.ok) cart = await response.json();
      } catch (_) { /* The drawer can refresh itself; the products are already added. */ }
      this.submitting = false; this.refreshCards(); this.renderSummary();
      this.querySelector('[data-box-cart-review]').hidden = false;
      const confirmation = addedCount === 1 ? (this.config.add_success_single || '1 item added to your cart.') : (this.config.add_success || '[count] items added to your cart.');
      this.announce(confirmation.replace('[count]', addedCount));
      document.dispatchEvent(new CustomEvent('tazty:cart-updated', { detail: cart }));
    }
  }
  customElements.define('tazty-box-builder', BoxBuilder);
})();
